import { Info } from 'lucide-react'
import { Toggle, Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@atrvd/saas-ui'
import type { SageBankAccountRow } from '@/lib/db-tenant'
import { sageIncludeLock, type SageIncludeLock } from './sageBankAccountsTable.utils'

const LOCK_TOOLTIP: Record<SageIncludeLock, string> = {
  missing:
    "Sage no longer returns this account, so it isn't counted. It's counted again if Sage returns it and it's switched on.",
  inactive:
    "Inactive in Sage, so it isn't counted. It's counted again if Sage reactivates it and it's switched on.",
}

interface SageIncludeToggleProps {
  account: SageBankAccountRow
  /** The user may not edit, or a write for this account is in flight. */
  disabled: boolean
  onChange: (include: boolean) => void
}

/**
 * The Include in Cash Position cell of the Sage bank-account table.
 *
 * An account missing in Sage or inactive there is never counted (see
 * `sageIncludeLock`), so its switch reads off, is disabled and says why. Only
 * the display changes: the stored flag is the user's intent and takes effect
 * again once Sage returns or reactivates the account.
 */
export function SageIncludeToggle({ account, disabled, onChange }: SageIncludeToggleProps) {
  const lock = sageIncludeLock(account)
  const on = account.include_in_cash_position && lock === null
  const toggle = (
    <span className="inline-flex items-center gap-2">
      <Toggle
        checked={on}
        disabled={lock !== null || disabled}
        size="sm"
        aria-label={`Include ${account.name} (${account.financial_entity_id}) in Cash Position`}
        onChange={onChange}
      />
      {/* The switch already tells assistive tech its state; this text is for sighted users. */}
      <span aria-hidden="true" className="text-sm text-muted-foreground">
        {on ? 'Yes' : 'No'}
      </span>
    </span>
  )
  if (lock === null) return toggle
  // A disabled switch takes no focus or pointer events, so the tooltip hangs on a focusable wrapper.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex cursor-not-allowed items-center gap-1.5">
          {toggle}
          <Info aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground" />
        </span>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent>{LOCK_TOOLTIP[lock]}</TooltipContent>
      </TooltipPortal>
    </Tooltip>
  )
}
